import type { OperationKey } from '@foodlista/operations'
import type { OperationRun } from '@foodlista/trigger-vue'
import type { OperationItem } from '../types'
import { isTerminalRunStatus } from '@foodlista/trigger-vue'
import { isOperationActive } from './lifecycle'
import { itemMatchesScope } from './matchScope'

export function restaurantSlugFromScope(
  scope: Record<string, unknown>,
): string | undefined {
  const slug = scope.restaurantSlug
  return typeof slug === 'string' ? slug : undefined
}

export function findActiveScopedOperation<Scope extends Record<string, unknown>>(
  items: OperationItem[],
  key: OperationKey,
  scope: Scope,
  now: Date,
  matchesScope: (item: OperationItem, scope: Scope) => boolean = (item, currentScope) =>
    itemMatchesScope(item, key, currentScope),
): OperationItem | null {
  const matches = items
    .filter(item =>
      item.type === key
      && isOperationActive(item, item.run, now)
      && matchesScope(item, scope))
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())

  return matches[0] ?? null
}

export function shouldHandleTerminalTransition(input: {
  operationId: string | null
  terminalHandled: boolean
  status: string | undefined
  previousStatus: string | undefined
}): boolean {
  if (!input.operationId || input.terminalHandled || !input.status) {
    return false
  }

  if (!isTerminalRunStatus(input.status)) {
    return false
  }

  if (
    input.previousStatus !== undefined
    && isTerminalRunStatus(input.previousStatus)
  ) {
    return false
  }

  return true
}

export function tokenAvailabilityError(input: {
  hasTracking: boolean
  hasToken: boolean
  tokenPending: boolean
  tokenError: Error | null
}): Error | null {
  if (!input.hasTracking) {
    return null
  }

  if (input.hasToken || input.tokenPending) {
    return null
  }

  if (input.tokenError) {
    return new Error('Realtime connection unavailable. Please try again.')
  }

  return null
}

export function resolveTerminalResult(
  status: string,
  key: string,
  run: OperationRun | null | undefined,
  resolve: (run: OperationRun) => unknown,
): { kind: 'completed', result: unknown } | { kind: 'failed', error: Error } {
  if (status === 'COMPLETED' && run) {
    return { kind: 'completed', result: resolve(run) }
  }

  return {
    kind: 'failed',
    error: new Error(`${key} ${status.toLowerCase()}`),
  }
}
